/**
 * Read-only modal hooks.
 *
 * `useModalUrlState` and `useModalLinks` derive state from the URL or build
 * URLs without triggering navigation. Split out from the main control hook so
 * each file stays focused and under the 300-line cap. No behavior changes.
 */

"use client";

import { useCallback, useMemo } from "react";
import { useSearchParams } from "next/navigation";
import {
  type ConfiguredModalId,
  type ModalParams,
  parseHubModalUrlState,
} from "../url/modal-url-config";
import {
  buildModalUrl,
  buildModalParams,
  type BuildModalUrlOptions,
} from "../url/modal-url-builder";
import { useHubModalController } from "./use-hub-modal-controller";

/**
 * Hook for getting modal state from URL
 *
 * Useful for components that need to read modal state without controlling it.
 *
 * @param modalId - Optional modal ID to check (if not provided, returns any modal in URL)
 * @returns Current modal state from URL
 *
 * @example
 * ```tsx
 * function PageTitle() {
 *   const modalState = useModalUrlState();
 *
 *   if (modalState.isOpen) {
 *     return <h1>Viewing {modalState.modalId}</h1>;
 *   }
 *
 *   return <h1>Dashboard</h1>;
 * }
 * ```
 */
export function useModalUrlState(modalId?: string) {
  const searchParams = useSearchParams();

  return useMemo(() => {
    const parsed = parseHubModalUrlState(searchParams);
    const scopedParsed = modalId
      ? parsed && parsed.modalId === modalId
        ? parsed
        : null
      : parsed;

    return {
      isOpen: scopedParsed !== null,
      modalId: scopedParsed?.modalId || null,
      params: scopedParsed?.params || null,
      entityId: scopedParsed?.entityId || null,
    };
  }, [searchParams, modalId]);
}

/**
 * Hook for building modal links
 *
 * Returns functions for building URLs without triggering navigation.
 * Useful for creating shareable links or link components.
 *
 * @example
 * ```tsx
 * function DealLink({ deal }) {
 *   const { buildUrl } = useModalLinks();
 *   const dealUrl = buildUrl('deals-detail', { dealsId: deal.id });
 *
 *   return (
 *     <Link href={dealUrl}>
 *       View Deal
 *     </Link>
 *   );
 * }
 * ```
 */
export function useModalLinks() {
  const searchParams = useSearchParams();
  const controller = useHubModalController();

  const buildUrl = useCallback(
    <T extends ConfiguredModalId>(
      modalId: T,
      params: ModalParams<T> & Record<string, string | undefined>,
      options?: Pick<BuildModalUrlOptions, "preserveParams" | "pathname">
    ) => {
      if (!options || options.preserveParams !== false) {
        return controller.getModalUrl(modalId, params);
      }
      return buildModalUrl(modalId, params, {
        searchParams,
        preserveParams: options?.preserveParams ?? true,
        pathname: options?.pathname,
      });
    },
    [controller, searchParams]
  );

  const buildParams = useCallback(
    <T extends ConfiguredModalId>(
      modalId: T,
      entityId?: string,
      additionalParams?: Record<string, string>
    ) => {
      return buildModalParams(modalId, entityId, additionalParams);
    },
    []
  );

  return {
    buildUrl,
    buildParams,
  };
}
